<script lang="ts">
  import { listCustomerProblems } from '@shared/api/customer-problems';
  import { listProblems } from '@shared/api/problems';
  import type { CustomerProblem } from '@shared/types/customer-problem';
  import type { SalesActionTarget } from '@shared/types/sales-action';

  import {
    buildIssueTagOptions,
    findIssueTagOption,
    withCurrentOption,
  } from './issue-tag-options';

  interface Props {
    target: SalesActionTarget;
    value: string;
    /// 現在値の課題名 (一覧応答同梱の `source_problem.problem_name`)。現在値が
    /// 画面ターゲットの通常候補外でもセレクトに残すためのラベル (86eya0dva PR3)。
    currentLabel?: string | null;
    disabled?: boolean;
    showDerived?: boolean;
    onChange: (value: string) => void;
  }

  let {
    target,
    value,
    currentLabel = null,
    disabled = false,
    showDerived = true,
    onChange,
  }: Props = $props();

  let selected = $state(value);
  let customerProblems = $state<CustomerProblem[]>([]);
  let problemNameById = $state<Map<string, string>>(new Map());
  let loading = $state(false);
  let error = $state<string | null>(null);
  let requestSeq = 0;

  $effect(() => {
    selected = value;
  });

  $effect(() => {
    void loadOptions(target.kind, target.id);
  });

  const options = $derived(
    buildIssueTagOptions(customerProblems, problemNameById),
  );
  // 現在値が通常候補外でもセレクトに残す (暗黙解除しない)。候補集合は変えない。
  const displayOptions = $derived(
    withCurrentOption(options, selected || null, currentLabel),
  );
  const selectedOption = $derived(findIssueTagOption(options, selected));
  const scoreLabel = $derived(
    selectedOption ? String(selectedOption.score) : '—',
  );
  const verificationStatusLabel = $derived(
    selectedOption ? selectedOption.verificationStatusLabel : '—',
  );
  // 現在値が候補外だがラベル解決済み (pin 表示中)。
  const pinnedCurrent = $derived(
    selected !== '' && selectedOption === undefined && currentLabel !== null,
  );
  const unresolved = $derived(
    selected !== '' &&
      !loading &&
      !error &&
      selectedOption === undefined &&
      !pinnedCurrent,
  );

  async function loadOptions(kind: SalesActionTarget['kind'], id: string) {
    const seq = ++requestSeq;
    loading = true;
    error = null;
    const query = kind === 'firm' ? { firm_id: id } : { lawyer_id: id };
    try {
      const [cps, names] = await Promise.all([
        listCustomerProblems({
          ...query,
          include_rejected: true,
          page_size: 200,
        }),
        fetchAllProblemNames(),
      ]);
      if (seq !== requestSeq) return;
      customerProblems = cps.items;
      problemNameById = names;
    } catch (err) {
      if (seq !== requestSeq) return;
      customerProblems = [];
      problemNameById = new Map();
      error = err instanceof Error ? err.message : String(err);
    } finally {
      if (seq === requestSeq) loading = false;
    }
  }

  /// 課題マスタは backend 側で page_size が 200 に clamp されるため、
  /// 200 件を超える場合でも取りこぼさないよう全ページ取得する。
  async function fetchAllProblemNames(): Promise<Map<string, string>> {
    const map = new Map<string, string>();
    const pageSize = 200;
    let page = 1;
    for (;;) {
      const res = await listProblems({ page, page_size: pageSize });
      for (const problem of res.items) map.set(problem.id, problem.name);
      if (res.items.length < pageSize || map.size >= res.total) break;
      page += 1;
    }
    return map;
  }

  function handleChange() {
    onChange(selected);
  }
</script>

<div class="issue-tag-field">
  <label>
    <span>課題</span>
    <select
      bind:value={selected}
      disabled={disabled || loading}
      onchange={handleChange}
    >
      <option value="">(なし)</option>
      {#each displayOptions as option (option.value)}
        <option value={option.value}>{option.label}</option>
      {/each}
    </select>
  </label>

  {#if showDerived}
    <div class="derived" aria-label="課題の派生表示">
      <div>
        <span class="derived-label">AI推認スコア</span>
        <span class="derived-value">{scoreLabel}</span>
      </div>
      <div>
        <span class="derived-label">確認状態</span>
        <span class="derived-value" class:denied={selectedOption?.denied}>
          {verificationStatusLabel}
        </span>
      </div>
    </div>
  {/if}

  {#if error}
    <small class="error">{error}</small>
  {:else if loading}
    <small class="muted">課題を読み込んでいます。</small>
  {:else if pinnedCurrent}
    <small class="muted">選択中の課題はこの画面の通常候補外です。</small>
  {:else if options.length === 0}
    <small class="muted">選択できる顧客課題はありません。</small>
  {:else if unresolved}
    <small class="muted">選択中の課題を解決できません。</small>
  {/if}
</div>

<style>
  .issue-tag-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  select {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: #fff;
    font: inherit;
  }
  select:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  .derived {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
  .derived > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
    padding: 6px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    background: #f9fafb;
  }
  .derived-label {
    color: #6b7280;
    font-size: 0.75rem;
  }
  .derived-value {
    color: #111827;
    font-weight: 700;
    white-space: nowrap;
  }
  .derived-value.denied {
    color: #9ca3af;
    text-decoration: line-through;
  }
  .muted {
    color: #6b7280;
    font-size: 0.8125rem;
  }
  .error {
    color: #b91c1c;
    font-size: 0.8125rem;
  }
</style>
